import type { Dispatch, PropsWithChildren, SetStateAction } from 'react';
import { createContext, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import zodToJsonSchema from 'zod-to-json-schema';
import { useRouter } from 'next/router';
import qs from 'qs';

import log from '~/common/utils/log';

import { getCreateNew } from '../../create/create.services';
import type { FormValues } from '../types/FormValues';
import { useAnonymousContestCreation } from '../../create/hooks/useAnonymousContestCreation';
import { getEditNew } from '../../edit/edit.services';
import { getRestartNew } from '../../restart/restart.services';
import { useContestLocationSettings } from '../hooks/useContestLocationSettings';
import type { RestartQueryParams } from '../api/settings/types/query.params';

import type { ContestManageStore } from './types/store';
import { type SectionKeyItem, getAllSectionKeys } from './utils/getAllSectionKeys';
import { getSectionValidationSchema } from './utils/getSectionValidationSchema';
import type { FormattedErrors } from './utils/formatZodErrors';
import { parseInitialResponse } from './parsers/parseInitialResponse';
import { storeReducer } from './store/store.reducer';
import type { StoreAction } from './store/types/actions';
import { createInitAction } from './store/store.actions';
import ManageContestQueryKeys from './consts/query';
import { formatValidationErrors } from './utils/formatValidationErrors';

export type TouchedValues = {
  [sectionKey: string]: {
    [fieldKey: string]: boolean;
  };
};

type ContestManageContextValue = {
  errors: FormattedErrors | null;
  store: ContestManageStore;
  dispatchStoreAction: Dispatch<StoreAction>;
  values: FormValues;
  setValues: Dispatch<SetStateAction<FormValues>>;
  sections: string[];
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  isTouched: boolean;
  currentSection: SectionKeyItem | null;
  validationSchema: z.ZodObject<any, any>;
  touched: TouchedValues;
  setCurrentSection: Dispatch<SetStateAction<SectionKeyItem | null>>;
  setTouched: Dispatch<SetStateAction<TouchedValues>>;
  allSectionsKeys: SectionKeyItem[];
  setOptionalFieldsOverrides: Dispatch<SetStateAction<{ [sectionKey: string]: string[] }>>;
  allowedTagNames: string[];
};

export const ContestManageContext = createContext<ContestManageContextValue>({
  store: {
    sectionsKeys: [],
    sections: {},
    fields: {},
    fieldsCache: {},
    sectionsCache: {},
  },
  errors: null,
  dispatchStoreAction: () => {},
  values: {},
  setValues: () => {},
  sections: [],
  isLoading: true,
  isError: false,
  error: null,
  isTouched: false,
  currentSection: null,
  validationSchema: z.object({}),
  touched: {},
  setTouched: () => {},
  setCurrentSection: () => {},
  allSectionsKeys: [],
  setOptionalFieldsOverrides: () => {},
  allowedTagNames: [],
});

const initialValue = {
  sectionsKeys: [],
  sections: {},
  fields: {},
  fieldsCache: {},
  sectionsCache: {},
};

export function ContestManageContextProvider({
  children,
  operation = 'create',
  restartData,
}: PropsWithChildren<{
  operation?: 'create' | 'edit' | 'restart';
  restartData?: RestartQueryParams;
}>) {
  const router = useRouter();
  const contestId = router.query?.contestUuid as string;

  const initialValues = useMemo<
    Partial<{ stepOne: { leagueId: string; contestTemplateId: string } }>
  >(() => qs.parse(qs.stringify(router.query, { encode: false })), [router.query]);
  const hasSetSection = useRef(false);

  const { value: anonValue } = useAnonymousContestCreation<{
    values?: FormValues;
    store?: ContestManageStore;
  }>();

  const [store, dispatchStoreAction] = useReducer(storeReducer, anonValue?.store || initialValue);
  const [currentSection, setCurrentSection] = useState<SectionKeyItem | null>();
  const [values, setValues] = useState<FormValues>(
    ['edit', 'restart'].includes(operation) ? {} : anonValue?.values || initialValues
  );
  const [touched, setTouched] = useState<TouchedValues>({});
  const allSectionsKeys = useMemo(() => getAllSectionKeys(store), [store]);
  const [optionalFieldsOverrides, setOptionalFieldsOverrides] = useState<{
    [sectionKey: string]: string[];
  }>({});

  const { allowedTagNames } = useContestLocationSettings();

  const validationSchema = useMemo(
    () =>
      z.object(
        allSectionsKeys
          .filter((section) => section.key !== 'initialStep')
          .reduce((acc, item) => {
            const sectionValidationSchema = getSectionValidationSchema(
              store,
              item.path.slice(-1)[0],
              optionalFieldsOverrides[item.key]
            );
            if (sectionValidationSchema) {
              acc[item.key] = sectionValidationSchema;
            }

            return acc;
          }, {})
      ),
    [allSectionsKeys, optionalFieldsOverrides, store]
  );

  const checkIsTouched = (touchedValues: TouchedValues): boolean =>
    Object.values(touchedValues).some((section) =>
      Object.values(section).some((isTouched) => isTouched)
    );

  useEffect(() => {
    if (!allSectionsKeys.length) {
      return;
    }

    // EO Integration through query params
    if (
      initialValues?.stepOne?.leagueId ||
      (initialValues?.stepOne?.leagueId && initialValues?.stepOne?.contestTemplateId)
    ) {
      if (hasSetSection.current) {
        setCurrentSection(allSectionsKeys[1]);
        hasSetSection.current = true;
      }
    }

    if (currentSection) {
      const currentVersion = allSectionsKeys.find((item) => item.key === currentSection?.key);
      if (!currentVersion) {
        const firstSectionInSections = allSectionsKeys.find((item) =>
          item.path.join(',').startsWith(currentSection.path.join(','))
        );
        if (!firstSectionInSections) {
          return;
        }
        setCurrentSection(firstSectionInSections);
        return;
      }
      if (JSON.stringify(currentVersion) !== JSON.stringify(currentSection)) {
        setCurrentSection(currentVersion);
      }
      return;
    }
    setCurrentSection(allSectionsKeys[0]);
  }, [allSectionsKeys, currentSection, initialValues, router, router.query]);

  const errors = useMemo(() => {
    const result = validationSchema.safeParse(values);
    return formatValidationErrors(result);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validationSchema, JSON.stringify(values)]);

  // eslint-disable-next-line local-rules/require-query-stale-time
  const { isLoading, isFetching, error } = useQuery({
    queryKey: (() => {
      switch (operation) {
        case 'edit':
          return ManageContestQueryKeys.EDIT_CONTEST(contestId);
        case 'restart':
          return ManageContestQueryKeys.RESTART_CONTEST(contestId);
        case 'create':
        default:
          return ManageContestQueryKeys.CREATE_CONTEST();
      }
    })(),
    queryFn: () => {
      switch (operation) {
        case 'edit':
          return getEditNew(contestId);
        case 'restart':
          return getRestartNew({
            ...restartData,
            allowedTagNames,
          });
        case 'create':
        default:
          return getCreateNew({
            allowedTagNames,
          });
      }
    },
    onSuccess: (data) => {
      const parsedData = parseInitialResponse(data);
      dispatchStoreAction(createInitAction(parsedData));
    },
    enabled: allowedTagNames?.length > 0,
  });

  log('context', {
    optionalFieldsOverrides,
    allSectionsKeys,
    errors,
    store,
    values,
    validationSchema: zodToJsonSchema(validationSchema),
  });

  const value = useMemo(
    () => ({
      store,
      dispatchStoreAction,
      currentSection,
      isError: !!error,
      isLoading: isLoading || isFetching,
      isTouched: checkIsTouched(touched),
      sections: store.sectionsKeys,
      setValues,
      values,
      validationSchema,
      errors,
      error,
      touched,
      setTouched,
      setCurrentSection,
      allSectionsKeys,
      setOptionalFieldsOverrides,
      allowedTagNames,
    }),
    [
      store,
      currentSection,
      error,
      isLoading,
      isFetching,
      values,
      validationSchema,
      errors,
      touched,
      allSectionsKeys,
      allowedTagNames,
    ]
  );

  return <ContestManageContext.Provider value={value}>{children}</ContestManageContext.Provider>;
}
